import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { config } from '../config';

/**
 * Initialize Datadog Real User Monitoring (RUM).
 *
 * RUM automatically captures:
 * - Page views and navigation timing (with React Router integration)
 * - User interactions (clicks, form submissions)
 * - XHR/Fetch requests with timing and errors
 * - JavaScript errors and stack traces
 * - Long tasks (browser main thread blocking)
 * - Core Web Vitals (LCP, FID, CLS)
 * - Resource loading performance (images, scripts, stylesheets)
 * - User sessions with full replay capability (if enabled)
 */
export const initDatadogRum = (): void => {
    const {
        datadogRumApplicationId,
        datadogRumClientToken,
        datadogRumSite,
        environment,
    } = config;

    if (environment !== 'production') {
        return;
    }

    if (!datadogRumApplicationId || !datadogRumClientToken) {
        return;
    }

    if (datadogRum.getInternalContext()) {
        return;
    }

    try {
        datadogRum.init({
            applicationId: datadogRumApplicationId,
            clientToken: datadogRumClientToken,
            site: datadogRumSite || 'datadoghq.com',
            service: 'northspyre-deal-frontend', // name configured in DataDog, if changing here, change there too
            env: environment,
            version: process.env.REACT_APP_VERSION || '1.0.0',
            sessionSampleRate: 100,
            sessionReplaySampleRate: 20,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',
            plugins: [reactPlugin({ router: true })],
        });

        console.log('Datadog RUM initialized successfully');
    } catch (error) {
        console.error('Failed to initialize Datadog RUM:', error);
    }
};

/**
 * Set the current user for RUM session tracking.
 * Call this after user authentication.
 */
export const setDatadogRumUser = (userId: string): void => {
    if (datadogRum.getInternalContext()) {
        datadogRum.setUser({
            id: userId,
        });
    }
};

/**
 * Add custom context to RUM events.
 * Useful for adding business-specific data like deal IDs, organization info, etc.
 */
export const setDatadogRumGlobalContext = (key: string, value: unknown): void => {
    if (datadogRum.getInternalContext()) {
        datadogRum.setGlobalContextProperty(key, value);
    }
};

/**
 * Track a custom action/event.
 * Use this for business-specific events like "deal_created", "proforma_exported", etc.
 */
export const trackDatadogRumAction = (
    name: string,
    context?: Record<string, unknown>,
): void => {
    if (datadogRum.getInternalContext()) {
        datadogRum.addAction(name, context);
    }
};

/**
 * Track a custom error.
 * Use this for caught errors that you want to report to Datadog.
 */
export const trackDatadogRumError = (
    error: Error,
    context?: Record<string, unknown>,
): void => {
    if (datadogRum.getInternalContext()) {
        datadogRum.addError(error, context);
    }
};
